import { ScenarioType } from '../state/interfaces'
import { ScenarioInput } from './interfaces'
import { sendGA4Event } from '../analytics/ga4'

export const COOKIE_STORAGE_KEY = 'scenario'
export const getPersistedScenarioInput = (): ScenarioInput | null => {
  const scenarioData = getCookie(COOKIE_STORAGE_KEY)
  if (!scenarioData) {
    return null
  }

  try {
    return JSON.parse(scenarioData)
  } catch (error) {
    throw new Error(`Could not parse JSON string within local storage key: '${COOKIE_STORAGE_KEY}'`)
  }
}

export const persistScenarioInput = (scenarioInput: ScenarioInput) => {
  setCookie(COOKIE_STORAGE_KEY, JSON.stringify(scenarioInput))
}

export const setCookie = (
  cookieName: string,
  cookieValue: string,
  expirationDays = 1,
  consentCategory: string | null = null,
) => {
  if (consentCategory === null || handleCookieYesConsent(consentCategory) == 'yes') {
    const date = new Date()
    const hostname = window.location.hostname
    date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000)
    const expires = 'expires=' + date.toUTCString()
    const domain = hostname === 'localhost' ? 'localhost' : '.planhub.ca'
    document.cookie = `${cookieName}=${cookieValue};${expires};path=/;domain=${domain}`
  }
}

export const getCookie = (cookieName: string) => {
  const name = `;${cookieName}=`
  let cookie = null
  const cookieArray = document.cookie.split(';')

  for (let i = 0, l = cookieArray.length; i < l; i++) {
    cookie = ';' + cookieArray[i].trim()
    while (cookie.charAt(0) === ' ') cookie = cookie.substring(1)
    if (cookie.indexOf(name) !== -1) return cookie.substring(name.length, cookie.length)
  }
  return ''
}

export const setAdbannerTargetingKeyValueCookie = function (newKeyValuePair: { key: string; value: string }) {
  const { key, value } = newKeyValuePair

  const adbannerTargetingKeyValueCookie: string | null = getCookie('adBannerTargetingKeyValues')

  if (adbannerTargetingKeyValueCookie) {
    adNewTargetingKeyValueCookie(adbannerTargetingKeyValueCookie, key, value)
  } else {
    const newCookieObject = {} as { [key: string]: string }
    newCookieObject[key] = value
    if (handleCookieYesConsent(ConsentCategories.Advertisement) == 'yes') {
      setCookie('adBannerTargetingKeyValues', JSON.stringify(newCookieObject), 1)
    }
  }
}

export const adNewTargetingKeyValueCookie = function (
  adbannerTargetingKeyValueCookie: string,
  key: string,
  value: string,
) {
  const cookieValue = JSON.parse(adbannerTargetingKeyValueCookie)
  cookieValue[key] = value
  if (handleCookieYesConsent(ConsentCategories.Advertisement) == 'yes') {
    setCookie('adBannerTargetingKeyValues', JSON.stringify(cookieValue), 1)
  }
}

export const setCurrentProvider = function ({ providerSlug, type }: { providerSlug: string; type: ScenarioType }) {
  if (type === ScenarioType.MOBILE) {
    setCookie('currentMobileProvider', providerSlug)
    sendGA4Event('GA4-current-provider-mobile', {
      current_provider: providerSlug,
    })
    setAdbannerTargetingKeyValueCookie({
      key: 'ActualProviderMobile',
      value: providerSlug,
    })
  } else if (type === ScenarioType.INTERNET) {
    setCookie('currentInternetProvider', providerSlug)
    sendGA4Event('GA4-current-provider-internet', {
      current_provider: providerSlug,
    })
    setAdbannerTargetingKeyValueCookie({
      key: 'ActualProviderInt',
      value: providerSlug,
    })
  }
}

export const setInternetSpeedSearched = function ({ speed }: { speed: string }) {
  setAdbannerTargetingKeyValueCookie({
    key: 'speed',
    value: speed,
  })
}

export const clearScenarioCookie = function (): void {
  const scenario = getCookie(COOKIE_STORAGE_KEY)
  if (scenario) setCookie(COOKIE_STORAGE_KEY, JSON.stringify('{}'))
}

export const handleCookieYesConsent = function (consentCategory: string) {
  const cookie = getCookie('cookieyes-consent')
  if (cookie === '') {
    return 'no'
  }

  return isConsentCategoryAllowed(consentCategory, cookie)
}

export const isConsentCategoryAllowed = function (consentCategory: string, cookie: string): string {
  const pairs = cookie.split(',')
  const parsedData: Record<string, string> = {}
  for (const pair of pairs) {
    const [key, value] = pair.split(':')
    if (key && value) {
      parsedData[key] = value
    }
  }

  return parsedData[consentCategory]
}

export const ConsentCategories = {
  Necessary: 'necessary',
  Functional: 'functional',
  Analytics: 'analytics',
  Performance: 'performance',
  Advertisement: 'advertisement',
  Uncategorized: 'uncategorized',
}

export function clearCookie(name: string) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`
}
